import type { QueryClient } from '@tanstack/react-query';
import type { DocumentPageListItem } from '@avisoma/shared';

type DocumentData = {
  status?: string;
  processing?: {
    pages?: { status?: string };
    pdfParser?: { status?: string };
    metadata?: { status?: string };
    embeddings?: { status?: string };
    classification?: { status?: string };
    reconstruction?: { status?: string };
    dataExtraction?: { status?: string };
  };
};

function isInFlightStatus(status: string | undefined): boolean {
  return status === 'processing' || status === 'pages-done' || status === 'pending';
}

function isDocumentPending(data: DocumentData | undefined): boolean {
  return !!(
    isInFlightStatus(data?.status) ||
    isInFlightStatus(data?.processing?.pages?.status) ||
    isInFlightStatus(data?.processing?.pdfParser?.status) ||
    isInFlightStatus(data?.processing?.metadata?.status) ||
    isInFlightStatus(data?.processing?.embeddings?.status) ||
    isInFlightStatus(data?.processing?.classification?.status) ||
    isInFlightStatus(data?.processing?.reconstruction?.status) ||
    isInFlightStatus(data?.processing?.dataExtraction?.status)
  );
}

/**
 * TanStack Query v5 `refetchInterval` callback for the document query.
 * Polls at 5 s while any processing step is pending; returns `false` once done.
 * Pass directly to `useQuery({ refetchInterval: documentRefetchInterval })`.
 */
export const documentRefetchInterval = (q: {
  state: { data: DocumentData | undefined; error: Error | null };
}) => {
  const data = q.state.data;
  // A failed fetch (e.g. 404) also has no data — don't poll the error screen forever.
  if (!data) return q.state.error ? false : 5000;
  return isDocumentPending(data) ? 5000 : false;
};

/**
 * Returns a TanStack Query v5 `refetchInterval` callback for the pages query.
 * Polls at 5 s while any page is still pending/processing OR while the document
 * itself is still pending (covers the empty-page case during splitting and
 * document-level retries where pages are settled but the document is not).
 * Returns `false` only when both the document and all pages are settled.
 *
 * Pass the result directly to `useQuery({ refetchInterval: makePagesRefetchInterval(...) })`.
 */
export function makePagesRefetchInterval(queryClient: QueryClient, id: string | undefined) {
  return (q: { state: { data: DocumentPageListItem[] | undefined; error: Error | null } }) => {
    const pages = q.state.data;
    const anyPagePending =
      pages != null &&
      pages.some((page) => page.status === 'processing' || page.status === 'pending');
    if (anyPagePending) return 5000;

    const cachedDocument = queryClient.getQueryData<DocumentData>(['document', id]);
    if (isDocumentPending(cachedDocument)) return 5000;

    // A failed fetch also has no data — don't poll the error screen forever.
    if (q.state.error) return false;

    // pages is undefined (not yet loaded) → keep polling
    if (pages == null) return 5000;

    return false;
  };
}

type StrandedQueryData = { count?: number };

/**
 * Returns a TanStack Query v5 `refetchInterval` callback for the stranded-pages
 * query. Polls at 5 s while the document itself is still pending, and at 15 s while
 * the document sits in `error` — the classic stranded state, where pages cross the
 * 30-minute stale gate long after processing stopped, so the Recover button must
 * appear without a page reload. The slower error cadence keeps the per-poll cost
 * down (the stranded endpoint reads every page's processing JSONB).
 *
 * Keep polling while the last payload still reports stranded pages, even when the
 * document is `done`. A false strand (empty JSONB under stage-write) would otherwise
 * freeze the page badge until a hard reload, because the global staleTime is 30 s
 * and this interval used to return false at `done`.
 *
 * Pass the result directly to `useQuery({ refetchInterval: makeStrandedRefetchInterval(...) })`.
 */
export function makeStrandedRefetchInterval(queryClient: QueryClient, id: string | undefined) {
  return (q: { state: { data: StrandedQueryData | undefined } }) => {
    const cachedDocument = queryClient.getQueryData<DocumentData>(['document', id]);
    if (isDocumentPending(cachedDocument)) return 5000;
    if (cachedDocument?.status === 'error') return 15000;
    if ((q.state.data?.count ?? 0) > 0) return 5000;
    return false;
  };
}
